import { useLocalStorage } from '@mantine/hooks'
import type { ReactNode } from 'react'
import { useCallback, useRef, useState } from 'react'
import type {
  CaptchaToken,
  VisibleCaptchaRef,
} from 'src/components/Account/Captcha/Captcha'
import { VisibleTurnstileWrapper } from 'src/components/Account/Captcha/TurnstileWrapper'
import { useNeedCommentTurnstile } from 'src/services/apollo/message'

export function useSendCommentWithTurnstile() {
  const turnstileRef = useRef<VisibleCaptchaRef>(null)
  const [captchaToken, setCaptchaToken] = useState<CaptchaToken>()

  // Backend is the authoritative source: it checks experiment group + user flags
  const {
    data: needTurnstileData,
    loading: isQuerying,
    error: queryError,
  } = useNeedCommentTurnstile(false)
  const [forceCommentTurnstile] = useLocalStorage<boolean>({
    key: 'debug::force-comment-turnstile',
    defaultValue: false,
  })
  const needTurnstile = forceCommentTurnstile || (needTurnstileData ?? false)

  const getTurnstileToken = useCallback((): string | null => {
    if (!needTurnstile) return null
    return captchaToken?.token ?? null
  }, [needTurnstile, captchaToken])

  const TurnstileWidget = useCallback(
    (): ReactNode =>
      needTurnstile ? (
        <VisibleTurnstileWrapper
          ref={turnstileRef}
          setToken={setCaptchaToken}
          action="sendComment"
        />
      ) : null,
    [needTurnstile],
  )

  // Fail-close: block send until query resolves without error and (if needed) token is ready
  const turnstileReady =
    !isQuerying &&
    !queryError &&
    (!needTurnstile || Boolean(captchaToken?.token))

  const resetTurnstile = useCallback(() => {
    turnstileRef.current?.reset?.()
    setCaptchaToken(undefined)
  }, [])

  return { getTurnstileToken, TurnstileWidget, turnstileReady, resetTurnstile }
}
